/**
 * The containers toolbar buttons live in. Each one declares, through
 * `EditorToolbarContext`, whether the buttons inside it show their labels —
 * so a button never takes a `hideName`-style prop and the two consumers can
 * no longer disagree about the same region.
 */

import type { Editor } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { useCurrentEditor } from "@tiptap/react";
import { BubbleMenu, type BubbleMenuProps } from "@tiptap/react/menus";
import { ChevronDownIcon } from "lucide-react";
import { type HTMLAttributes, useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { lastKnownRectAnchor } from "./anchor";
import { EditorToolbarContext } from "./toolbar-context";

/**
 * Deliberately not `editor.isFocused`: focus leaves the editor when the Format
 * dropdown opens in a portal, which would unmount the bubble the dropdown is
 * anchored to.
 */
export const shouldShowBubbleMenu = (editor: Editor) => {
  const { selection } = editor.state;
  // A selected block (a smart phrase) has no text of its own to format.
  if (selection instanceof NodeSelection) return false;
  return selection.from !== selection.to;
};

export type EditorBubbleMenuProps = Omit<BubbleMenuProps, "editor">;

export const EditorBubbleMenu = ({ className, children, ...props }: EditorBubbleMenuProps) => {
  const { editor } = useCurrentEditor();
  return (
    <BubbleMenu
      // z-50 because the bubble is a popup and belongs over the app chrome.
      // Tiptap renders it in the content flow with no z-index at all, so the
      // sticky tab bar (z-10) painted over it whenever the selected line sat
      // near the top of the scroll container: every button was still visible,
      // and every click landed on a tab instead — which navigates away from
      // the page being edited. Nothing between here and the root creates a
      // stacking context, so the z-index is all it takes.
      //
      // No first/last-child radius overrides: those squared the inner corners
      // to fake one segmented bar, which fought every button's own rounding.
      // Each button rounds itself.
      className={cn("z-50 flex rounded-xl border bg-background p-0.5 shadow", className)}
      editor={editor ?? undefined}
      shouldShow={({ editor: ed }) => shouldShowBubbleMenu(ed)}
      {...props}
    >
      {/* Children are rendered exactly as composed. This used to inject a
          Separator before every child, which stacked three of them wherever a
          caller placed its own and divided the icon row nobody asked to
          divide. Placing separators is the caller's job. */}
      <EditorToolbarContext value={{ labels: false }}>{children}</EditorToolbarContext>
    </BubbleMenu>
  );
};

export type EditorSelectorProps = HTMLAttributes<HTMLDivElement> & {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
};

export const EditorSelector = ({ open, onOpenChange, title, className, children, ...props }: EditorSelectorProps) => {
  const { editor } = useCurrentEditor();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // The trigger leaves the DOM with the bubble menu while this popup is still
  // fading out; see anchor.ts for what that does to an unguarded anchor.
  const anchor = useMemo(() => lastKnownRectAnchor(() => triggerRef.current), []);

  const isOpen = open ?? uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  // Applying a format dismisses the list. Without this the bubble re-anchors
  // under the changed line and drags the still-open dropdown with it.
  const toolbar = useMemo(() => ({ labels: true, onCommand: () => setOpen(false) }), [setOpen]);

  if (!editor) {
    return null;
  }

  return (
    <Popover onOpenChange={setOpen} open={isOpen}>
      <PopoverTrigger
        ref={triggerRef}
        render={<Button className="h-7 gap-1 rounded-md border-none px-2" size="sm" variant="ghost" />}
      >
        <span className="whitespace-nowrap text-xs">{title}</span>
        <ChevronDownIcon size={12} />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        anchor={anchor}
        // gap-0 overrides PopoverContent's default gap-4, which put 16px between
        // every menu row and made a six-item list 280px tall.
        className={cn("w-44 gap-0 p-1", className)}
        sideOffset={5}
        // Don't move focus into the popup on open — would blur the editor and
        // cause the BubbleMenu (the popover's anchor) to unmount, leaving the
        // popover stranded at viewport (0,0).
        initialFocus={false}
        {...props}
      >
        <EditorToolbarContext value={toolbar}>{children}</EditorToolbarContext>
      </PopoverContent>
    </Popover>
  );
};
